import React, { useState } from "react";
import EmojiPicker from "emoji-picker-react";
import { Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Smiley button that opens a full standard-emoji picker (categories + search +
 * skin tones).
 *
 * Two insertion modes:
 *  - Bound input  : pass `targetRef` (ref to the <input>/<textarea>) + `value` +
 *    `onChange`. The emoji is inserted at the caret and focus/caret restored.
 *  - Custom handler: pass `onPick(emojiChar)` (e.g. to insert into a TipTap editor).
 *
 * The panel sits at z-[90] so it always renders ABOVE the docked chat (z-[70]).
 */
export function EmojiPickerButton({
  targetRef, value, onChange, onPick,
  side = "top", align = "end", className, testId = "emoji-picker-button",
}) {
  const [open, setOpen] = useState(false);

  const insert = (emoji) => {
    if (onPick) { onPick(emoji); return; }
    const el = targetRef && targetRef.current;
    const cur = value || "";
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = cur.slice(0, start) + emoji + cur.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        try {
          el.focus();
          const pos = start + emoji.length;
          el.setSelectionRange(pos, pos);
        } catch (_) { /* caret restore best-effort */ }
      });
    } else {
      onChange(cur + emoji);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground", className)}
          aria-label="Add emoji"
          data-testid={testId}
        >
          <Smile className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={8} collisionPadding={8} className="z-[90] w-auto border-0 p-0 shadow-xl" data-testid="emoji-picker-panel">
        <EmojiPicker
          onEmojiClick={(e) => insert(e.emoji)}
          lazyLoadEmojis
          width={300}
          height={380}
          previewConfig={{ showPreview: false }}
        />
      </PopoverContent>
    </Popover>
  );
}
